import { FC } from 'react';
import {
	FirebaseAuthentication,
	SignInResult,
} from '@capacitor-firebase/authentication';
import {
	FacebookAuthProvider,
	getAuth,
	signInWithCredential,
	UserCredential,
} from 'firebase/auth';

import { usePendingCredentialStore } from '@mopla/business-logic';

import { FacebookButton } from '../../molecules/SocialButtons';

import { SocialLoginError } from './SocialLoginError';
import {
	ISocialLoginResult,
	SocialLoginErrorCodeEnum,
	SocialProviderEnum,
	TPushEvent,
} from './types';

type LoginWithFacebookProps = {
	onSignIn: (callback: () => Promise<ISocialLoginResult>) => Promise<void>;
	pushAuthEvent?: TPushEvent;
	isRegistration?: boolean;
};

export const LoginWithFacebook: FC<LoginWithFacebookProps> = ({
	onSignIn,
	pushAuthEvent,
	isRegistration,
}) => {
	const signInWithFacebook = async () => {
		await FirebaseAuthentication.signOut();
		const { setPendingCredential } = usePendingCredentialStore.getState();
		const auth = getAuth();
		if (pushAuthEvent) {
			pushAuthEvent(
				isRegistration ? 'sign_up_attempt' : 'login_attempt',
				'facebook'
			);
		}
		try {
			const result: SignInResult =
				await FirebaseAuthentication.signInWithFacebook();
			if (!result.credential?.accessToken) {
				throw new SocialLoginError(SocialLoginErrorCodeEnum.LOGIN_FAILED, {
					cause: 'Missing accessToken',
				});
			}
			const credential = FacebookAuthProvider.credential(
				result.credential?.accessToken
			);
			const signInResult: UserCredential = await signInWithCredential(
				auth,
				credential
			);
			if (!signInResult.user) {
				throw new SocialLoginError(SocialLoginErrorCodeEnum.LOGIN_FAILED, {
					cause: 'No user returned',
				});
			}
			if (pushAuthEvent) {
				pushAuthEvent(isRegistration ? 'sign_up' : 'login', 'facebook');
			}
			return {
				user: signInResult.user,
				providerId: SocialProviderEnum.FACEBOOK,
			};
		} catch (err: any) {
			if (err.code === 'auth/account-exists-with-different-credential') {
				const pendingCred = FacebookAuthProvider.credentialFromError(err);
				const email = err?.customData?.email;
				const tokenFromErr = pendingCred?.accessToken;

				if (!pendingCred || !tokenFromErr || !email) {
					throw new SocialLoginError(SocialLoginErrorCodeEnum.LOGIN_FAILED, {
						cause: 'No pending credential found',
					});
				}
				setPendingCredential({
					providerId: SocialProviderEnum.FACEBOOK,
					token: tokenFromErr,
					email,
				});
				throw new SocialLoginError(
					SocialLoginErrorCodeEnum.DIFFERENT_CREDENTIAL_EXISTS,
					{
						cause: err,
					}
				);
			}
			throw err;
		}
	};
	return <FacebookButton onClick={() => onSignIn(signInWithFacebook)} />;
};
